import React, { useMemo, useState, Fragment } from 'react';
import { GripVertical, Edit3, Trash2, Plus, ChefHat, ChevronDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCategories, Category } from './CategoriesContext';
import { useBranches, KitchenStation } from './BranchesContext';
import { StationIconBadge } from './BranchStationsSection';
export function CategoriesPanel({
  selectedId,
  onSelect
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const {
    categories,
    addCategory,
    updateCategory,
    removeCategory
  } = useCategories();
  const {
    branches
  } = useBranches();
  const [isAdding, setIsAdding] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [stationPickerOpenFor, setStationPickerOpenFor] = useState<string | null>(null);
  // Aggregate all unique stations across all branches
  const allStations = useMemo(() => {
    const map = new Map<string, KitchenStation>();
    branches.forEach(b => {
      b.stations.forEach(s => {
        if (!map.has(s.id)) map.set(s.id, s);
      });
    });
    return Array.from(map.values());
  }, [branches]);
  const handleAdd = () => {
    if (newCatName.trim()) {
      addCategory(newCatName.trim());
      setNewCatName('');
      setIsAdding(false);
    }
  };
  return <div className="flex flex-col gap-4" data-id="element-952">
      <div className="flex items-center justify-between mb-1" data-id="element-953">
        <h2 className="text-lg font-bold text-gray-900" data-id="element-954">Categories</h2>
        <button onClick={() => setIsAdding(true)} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2" data-id="element-955">
          <Plus size={16} data-id="element-956" />
          Add Category
        </button>
      </div>

      <div className="flex flex-col gap-3" data-id="element-957">
        {isAdding && <div className="flex items-center gap-2 p-3 rounded-xl border border-orange-300 bg-orange-50 shadow-sm" data-id="element-958">
            <input type="text" autoFocus value={newCatName} onChange={e => setNewCatName(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') handleAdd();
          if (e.key === 'Escape') setIsAdding(false);
        }} placeholder="Category name..." className="flex-1 px-3 py-1.5 rounded-lg border border-orange-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" data-id="element-959" />
            <button onClick={handleAdd} className="p-1.5 text-orange-600 hover:bg-orange-100 rounded-md" data-id="element-960">
              <Check size={18} data-id="element-961" />
            </button>
          </div>}

        {categories.map(cat => {
        const isSelected = cat.id === selectedId;
        const station = allStations.find(s => s.id === cat.defaultStationId);
        return <div key={cat.id} className={`relative flex items-center justify-between p-3 rounded-xl border transition-all ${isSelected ? 'bg-[#fef3c7] border-yellow-300 shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'}`} data-id="element-962">
              <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => onSelect(cat.id)} data-id="element-963">
                <GripVertical size={18} className="text-gray-300 cursor-grab hover:text-gray-500" data-id="element-964" />
                <div className="flex-1 min-w-0" data-id="element-965">
                  <div className={`font-semibold text-sm truncate ${isSelected ? 'text-gray-900' : 'text-gray-800'}`} data-id="element-966">
                    {cat.name}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2" data-id="element-967">
                    <span className={cat.active ? 'text-emerald-600 font-medium' : 'text-gray-400'} data-id="element-968">
                      {cat.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0 ml-2" data-id="element-969">
                {/* Station Picker Trigger — icon-only chip */}
                <div className="relative" data-id="element-970">
                  <button onClick={e => {
                e.stopPropagation();
                setStationPickerOpenFor(stationPickerOpenFor === cat.id ? null : cat.id);
              }} title={station ? `Routes to ${station.name}` : 'Set default kitchen station'} className={`flex items-center gap-1 px-1.5 py-1 rounded-md transition-colors border ${station ? 'bg-white border-gray-200 hover:bg-gray-50' : 'bg-gray-50 border-dashed border-gray-300 text-gray-400 hover:text-orange-600 hover:border-orange-300'}`} data-id="element-971">
                    {station ? <Fragment data-id="element-972">
                        <StationIconBadge icon={station.icon} color={station.color} size={10} data-id="element-973" />
                        <ChevronDown size={10} className="text-gray-400" data-id="element-974" />
                      </Fragment> : <ChefHat size={12} data-id="element-975" />}
                  </button>

                  {/* Station Picker Popover */}
                  <AnimatePresence data-id="element-976">
                    {stationPickerOpenFor === cat.id && <motion.div initial={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} transition={{
                  duration: 0.15
                }} className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border border-gray-200 z-50 py-1 overflow-hidden" data-id="element-977">
                        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50" data-id="element-978">
                          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide" data-id="element-979">
                            Default Station
                          </div>
                          <div className="text-[10px] text-gray-400 leading-tight mt-0.5" data-id="element-980">
                            Items in this category route here by default.
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto p-1" data-id="element-981">
                          {allStations.map(s => <button key={s.id} onClick={() => {
                      updateCategory(cat.id, {
                        defaultStationId: s.id
                      });
                      setStationPickerOpenFor(null);
                    }} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors ${cat.defaultStationId === s.id ? 'bg-orange-50 text-orange-700' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-982">
                              <StationIconBadge icon={s.icon} color={s.color} size={12} data-id="element-983" />
                              {s.name}
                              {cat.defaultStationId === s.id && <Check size={12} className="ml-auto text-orange-500" data-id="element-984" />}
                            </button>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <button className="text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-colors p-1.5 rounded-md" title="Edit category" data-id="element-985">
                  <Edit3 size={14} data-id="element-986" />
                </button>
                <button onClick={() => removeCategory(cat.id)} className="text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors p-1.5 rounded-md" title="Remove category" data-id="element-987">
                  <Trash2 size={14} data-id="element-988" />
                </button>
              </div>
            </div>;
      })}
      </div>

      {/* Click outside overlay for popover */}
      {stationPickerOpenFor && <div className="fixed inset-0 z-40" onClick={() => setStationPickerOpenFor(null)} data-id="element-989" />}
    </div>;
}